import is from 'ramda/src/is'
import { memo, useState, useEffect } from 'react'
import classNames from 'classnames'
import withStyles from '@material-ui/core/styles/withStyles'
import TextField from '@material-ui/core/TextField'
import InputAdornment from '@material-ui/core/InputAdornment'
import replace from 'ramda/src/replace'
import Body from 'root/src/client/web/typography/Body'
import { orNull } from 'root/src/shared/util/ramdaPlus'
import textFieldSetInputHandler from 'root/src/client/logic/form/handlers/textFieldSetInputHandler'
import onBlurHandler from 'root/src/client/logic/form/handlers/onBlurHandler'
import textFieldSetOnClickHandler from 'root/src/client/logic/form/handlers/textFieldOnClickHandler'
import MinMaxLength from 'root/src/client/web/base/MinMaxLength'
import { colorsNew, errorColor, linkColor, primaryGradient } from 'root/src/client/web/commonStyles'
import LoadingButton from 'root/src/client/web/base/LoadingButton'
import useDebounce from 'root/src/client/web/hooks/useDebounce'
import { handleNumericKeyPress } from 'root/src/client/util/handleNumericKeyPress'
import TertiaryBody from 'root/src/client/web/typography/TertiaryBody'
import Toggle from '../../base/Toggle'

const styles = {
	wrapperDiv: {
		position: 'relative',
		'&:hover': {
			borderBottom: `1px solid ${linkColor}`,
			marginBottom: -1,
		},
		'&:focus-within': {
			borderBottom: `1px solid ${linkColor}`,
			marginBottom: -1,
		},
	},
	flexContainer: {
		display: 'flex',
	},
	inputFlexGrow: {
		flexGrow: 1,
	},
	prefixTextContainer: {
		margin: [[0, -9, 0, 10]],
		padding: [[10, 0, 10, 0]],
		height: 19,
	},
	input: {
		padding: 0,
		color: 'white !important',
		// borderColor: secondaryColor,
		position: 'relative',
		borderRadius: 4,
		border: 'none',
		backgroundColor: colorsNew.dark1,
		'& input, textarea, select': {
			padding: 10,
			borderRadius: 2,
			borderBottom: '1px solid transparent',
			// don't like hack below but looks like it's necessary
			// to remove default browser behavior anybody better ideas?
			transitionProperty: 'border-bottom, background-color, font-size',
			transitionDuration: '0.2s, 5000000s, 5000000s',
			'&:-internal-autofill-selected, &:-webkit-autofill': {
				'-webkit-text-fill-color': 'white',
			},
		},
		'&::after, &::before': {
			borderBottomLeftRadius: 3,
			borderBottomRightRadius: 3,
		},
		'&::placeholder': {
			color: 'inherit',
			opacity: 1,
		},
	},
	inputLocked: {
		'& input, textarea, select': {
			cursor: 'not-allowed',
			'&:hover': {
				borderBottom: '1px solid transparent',
			},
			'&:focus': {
				borderBottom: '1px solid transparent',
			},
		},
	},
	label: {
		color: linkColor,
		zIndex: 1,
		padding: [[5, 5, 5, 10]],
		opacity: 0.42,
		cursor: 'text',
	},
	labelFocused: {
		color: 'white !important',
		transform: 'translate(0, -5px) scale(0.75)',
		opacity: 1,
	},
	inputError: {
		borderBottom: `1px solid ${errorColor} !important`,
	},
	white: {
		color: 'white !important',
	},
	error: {
		color: `${errorColor} !important`,
	},
	fieldMax: {
		position: 'absolute',
		right: 0,
	},
	subFieldText: {
		marginTop: 4,
		marginBottom: 0,
		color: '#04172D !important',
	},
	toggle: {
		position: 'absolute',
		top: -30,
		right: 0,
	},
}

const buttonStyles = {
	buttonAction: {
		backgroundImage: primaryGradient,
		borderRadius: 4,
		lineHeight: 1,
		height: 40,
		width: 160,
		marginLeft: 12,
		textTransform: 'none',
	},
	loadingContainer: {
		margin: [[0, 3]],
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
	},
}

export const FieldActionButton = withStyles(buttonStyles, { name: 'MuiFieldActionButton' })(
	({
		actionFn, action, moduleId, moduleKey, fieldId, label, value, classes,
	}) => {
		const [loading, setLoading] = useState(false)
		return (
			<LoadingButton
				className={classes.loadingContainer}
				isLoading={loading}
				disabled={loading}
				onClick={textFieldSetOnClickHandler(
					value,
					moduleId,
					moduleKey,
					fieldId,
					actionFn,
					action,
					setLoading,
				)}
			>{label}
			</LoadingButton>
		)
	},
)

const customNumericInputs = ['number', 'nonNegativeNumber']

const getInputType = (type) => {
	if (customNumericInputs.includes(type)) {
		return 'number'
	}
	return type
}

const StyledTextField = memo(({
	moduleKey, fieldId, fieldPath, setInput, fieldValue, fieldLabel, fieldError, fieldInfo,
	fieldHasError, fieldType, fieldMultiline, fieldPlaceholder, fieldDefaultStaticText,
	fieldMax, fieldMultilineRows, fieldHint, onBlur, moduleId, classes,
	fieldFullWidth = true, fieldActionButton, authentication, fieldAuthentication,
	fieldUnauthText, autoComplete, inputRef, locked, disabled, onExit, dispatcher,
	minRows, maxRows, fieldActionButtonFn, fieldTheme, inputProps, debounceInput, fieldToggle,
}) => {
	const [message, setMessage] = useState(null)
	const [text, setText] = useState(is(Object, fieldValue) ? fieldValue.value : fieldValue)
	const [customized, setCustomized] = useState(fieldValue?.[fieldToggle?.propName])

	// do not use debouncedText in places other than 2 arg of useEffect, it breaks complex fields
	const debouncedText = useDebounce(text, debounceInput ? 500 : 0)

	useEffect(() => {
		textFieldSetInputHandler(
			moduleKey,
			fieldPath,
			locked ? () => { } : setInput,
			fieldType,
			locked,
			fieldDefaultStaticText,
			text,
			{ propName: fieldToggle?.propName, customized },
		)()
	}, [debouncedText, customized])

	useEffect(() => {
		if (fieldDefaultStaticText) {
			setText(replace(fieldDefaultStaticText, '', fieldValue))
		} else {
			setText(is(Object, fieldValue) ? fieldValue.value : fieldValue)
			setCustomized(fieldValue?.[fieldToggle?.propName])
		}
	}, [fieldValue])

	const handleOnFocus = () => {
		if (!!authentication !== !!fieldAuthentication && fieldUnauthText) {
			const helperText = <Body>{fieldUnauthText}</Body>
			setMessage(helperText)
		}
	}

	const handleOnChange = (e) => {
		if (customNumericInputs.includes(fieldType)) {
			setText(e.currentTarget.valueAsNumber)
			return
		}
		setText(e.currentTarget.value)
	}

	const helperText = message || fieldError || fieldInfo || fieldHint
	return (
		<>
			<div className={classNames(
				!fieldFullWidth && classes.flexContainer, !fieldHasError && classes.wrapperDiv,
			)}
			>
				{orNull(
					fieldToggle,
					<div className={classes.toggle}>
						<Toggle
							{...fieldToggle}
							checked={customized}
							onChange={() => {
								setCustomized(prev => !prev)
							}}
						/>
					</div>,
				)}
				<TextField
					onKeyPress={handleNumericKeyPress}
					inputRef={inputRef}
					classes={(!fieldFullWidth) ? {
						root: classes.inputFlexGrow,
					} : null}
					fullWidth={fieldFullWidth}
					id={fieldId}
					label={fieldLabel}
					minRows={minRows}
					maxRows={maxRows}
					type={getInputType(fieldType)}
					disabled={locked || disabled}
					multiline={fieldMultiline}
					rows={fieldMultilineRows || 1}
					value={text?.toString()}
					error={fieldHasError}
					helperText={helperText}
					placeholder={fieldPlaceholder}
					onFocus={handleOnFocus}
					onChange={handleOnChange}
					onBlur={onBlurHandler(moduleId, moduleKey, onBlur, onExit, dispatcher)}
					autoComplete={autoComplete}
					InputLabelProps={{
						className: classes.label,
						classes: {
							focused: classes.labelFocused,
							shrink: classes.labelFocused,
						},
					}}
					InputProps={{
						startAdornment: fieldDefaultStaticText && (
							<InputAdornment
								className={classes.prefixTextContainer}
								disablePointerEvents="false"
								position="start"
							>
								<span>{fieldDefaultStaticText}</span>
							</InputAdornment>
						),
						className: classNames(
							classes.input,
							locked && classes.inputLocked,
							fieldHasError && classes.inputError,
							fieldHasError && classes.input,
						),
						classes: {
							error: classes.error,
							disabled: locked && classes.inputLocked,
						},
						disableUnderline: true,
						style: {
							backgroundColor: fieldTheme?.backgroundColor,
						},
					}}
					// eslint-disable-next-line react/jsx-no-duplicate-props
					inputProps={{
						...inputProps,
						'data-gramm': true,
					}}
					FormHelperTextProps={{
						classes: {
							error: classes.error,
							root: classes.white,
						},
					}}
				/>
				{
					fieldActionButton && (
						<FieldActionButton
							label={fieldActionButton.label}
							action={fieldActionButton.action}
							actionFn={fieldActionButtonFn}
							fieldId={fieldId}
							value={fieldValue}
							moduleKey={moduleKey}
							moduleId={moduleId}
						/>
					)
				}
				{orNull(
					fieldMax,
					<div className={classes.fieldMax}>
						<MinMaxLength>{`${fieldValue?.length ?? 0}/${fieldMax}`}</MinMaxLength>
					</div>,
				)}
			</div>
			{orNull(
				customized,
				<div
					className={classes.subFieldText}
				>
					<TertiaryBody
						className={classes.subFieldText}
					>
						{fieldToggle?.subFieldText}
					</TertiaryBody>
				</div>,
			)}
		</>
	)
})

export default withStyles(styles, { name: 'MuiStyledTextField' })(StyledTextField)
